import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Facebook, Instagram,  Menu, X, LayoutDashboard, UserRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { scrollToEl } from "@/lib/scroll";
import { Logo } from "@/components/Logo";

export const Navbar = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const goManifesto = () => {
    setOpen(false);
    if (location.pathname === "/") scrollToEl("#manifesto");
    else navigate("/#manifesto");
  };

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200/80 bg-white/85 shadow-sm backdrop-blur-xl print:hidden">
      <div className="mx-auto flex h-24 max-w-7xl items-center justify-between px-4 sm:px-8 lg:px-16">
        <Logo />
        <nav className="hidden items-center gap-8 md:flex">
          <Link
            to="/catalog"
            data-testid="nav-catalog-link"
            className="text-sm font-medium text-slate-600 transition-colors duration-200 hover:text-royal"
          >
            Catalog
          </Link>
          <button
            onClick={goManifesto}
            data-testid="nav-manifesto-link"
            className="text-sm font-medium text-slate-600 transition-colors duration-200 hover:text-royal"
          >
            Manifesto
          </button>
          <Link
            to="/reviews"
            data-testid="nav-reviews-link"
            className="text-sm font-medium text-slate-600 transition-colors duration-200 hover:text-royal"
          >
            Reviews
          </Link>
          {user && user.role === "admin" && (
            <Link
              to="/admin"
              data-testid="nav-admin-link"
              className="flex items-center gap-1.5 text-sm font-medium text-slate-600 transition-colors duration-200 hover:text-royal"
            >
              <LayoutDashboard className="h-4 w-4" /> Admin
            </Link>
          )}
        </nav>
        <div className="hidden items-center gap-3 md:flex">
          <div className="flex items-center gap-1.5 border-r border-slate-200 pr-3">
            <a
              href="https://instagram.com/oregontoterentals"
              target="_blank"
              rel="noreferrer"
              aria-label="Oregon Tote Rentals on Instagram"
              data-testid="nav-instagram-link"
              className="grid h-9 w-9 place-items-center rounded-full border border-slate-200 text-slate-500 transition-all duration-200 hover:border-royal hover:text-royal"
            >
              <Instagram className="h-4 w-4" />
            </a>
            <a
              href="https://facebook.com/oregontoterentals"
              target="_blank"
              rel="noreferrer"
              aria-label="Oregon Tote Rentals on Facebook"
              data-testid="nav-facebook-link"
              className="grid h-9 w-9 place-items-center rounded-full border border-slate-200 text-slate-500 transition-all duration-200 hover:border-royal hover:text-royal"
            >
              <Facebook className="h-4 w-4" />
            </a>
          </div>
          {user ? (
            <Link
              to="/account"
              data-testid="nav-account-link"
              className="flex items-center gap-2.5 rounded-full border border-slate-200 bg-white py-1.5 pl-1.5 pr-4 transition-all duration-200 hover:border-royal/50 hover:shadow-md hover:shadow-royal/10"
            >
              <span className="grid h-8 w-8 place-items-center rounded-full bg-royal font-display text-sm font-bold text-white">
                {(user.name || user.email)[0].toUpperCase()}
              </span>
              <span className="text-sm font-semibold text-ink">
                {user.name?.split(" ")[0] || "Account"}
              </span>
            </Link>
          ) : (
            <Link
              to="/auth"
              data-testid="nav-signin-button"
              className="flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:scale-[1.02] hover:bg-royal hover:shadow-lg hover:shadow-royal/25 active:scale-[0.98]"
            >
              <UserRound className="h-4 w-4" /> Sign in
            </Link>
          )}
        </div>
        <button
          className="grid h-10 w-10 place-items-center rounded-full border border-slate-200 md:hidden"
          onClick={() => setOpen(!open)}
          data-testid="nav-menu-button"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="overflow-hidden border-t border-slate-200 bg-white md:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              <Link
                to="/catalog"
                onClick={() => setOpen(false)}
                className="rounded-lg px-3 py-3 text-sm font-medium text-slate-700 hover:bg-royal-light"
              >
                Catalog
              </Link>
              <button
                onClick={goManifesto}
                className="rounded-lg px-3 py-3 text-left text-sm font-medium text-slate-700 hover:bg-royal-light"
              >
                Manifesto
              </button>
              {user && user.role === "admin" && (
                <Link
                  to="/admin"
                  onClick={() => setOpen(false)}
                  className="rounded-lg px-3 py-3 text-sm font-medium text-slate-700 hover:bg-royal-light"
                >
                  Admin
                </Link>
              )}
              <div className="mt-2 flex items-center gap-2.5 px-3">
                <a
                  href="https://instagram.com/oregontoterentals"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Oregon Tote Rentals on Instagram"
                  data-testid="nav-instagram-link-mobile"
                  className="grid h-10 w-10 place-items-center rounded-full border border-slate-200 text-slate-500 transition-colors duration-200 hover:border-royal hover:text-royal"
                >
                  <Instagram className="h-4.5 w-4.5" />
                </a>
                <a
                  href="https://facebook.com/oregontoterentals"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Oregon Tote Rentals on Facebook"
                  data-testid="nav-facebook-link-mobile"
                  className="grid h-10 w-10 place-items-center rounded-full border border-slate-200 text-slate-500 transition-colors duration-200 hover:border-royal hover:text-royal"
                >
                  <Facebook className="h-4.5 w-4.5" />
                </a>
              </div>
              <Link
                to={user ? "/account" : "/auth"}
                onClick={() => setOpen(false)}
                className="mt-2 rounded-full bg-ink px-5 py-3 text-center text-sm font-semibold text-white"
              >
                {user ? "My account" : "Sign in"}
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
